/**
 * TemplateZoneEditor
 *
 * Visual editor for placing content zones on a 1-page pitch template PDF.
 * The admin sees the rendered PDF and can add/move/resize rectangles for
 * each dynamic field (photo, name, title, searchQuestion, message, recommendation).
 *
 * All positions are stored as percentages (0–100) of the template dimensions
 * so they scale correctly to any beamer resolution.
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { TemplateZone, TemplateZoneField } from '../networkClubTypes';
import { loadPdfDocumentFromUrl } from '../loadPdfDocumentFromUrl';

// ── Zone field metadata ──────────────────────────────────────────────────────

const ZONE_FIELDS: { field: TemplateZoneField; label: string; icon: string; defaultColor: string }[] = [
  { field: 'photo',            label: 'Foto',          icon: '📷', defaultColor: '#ffffff' },
  { field: 'name',             label: 'Naam',          icon: '👤', defaultColor: '#ffffff' },
  { field: 'title',            label: 'Functie/Titel', icon: '💼', defaultColor: '#ffffff' },
  { field: 'searchQuestion',   label: 'Zoekvraag',     icon: '🔍', defaultColor: '#fde68a' },
  { field: 'message',          label: 'Boodschap',     icon: '💬', defaultColor: '#ffffff' },
  { field: 'recommendation',   label: 'Aanbeveling',   icon: '⭐', defaultColor: '#fde68a' },
];

const ZONE_COLORS: Record<TemplateZoneField, string> = {
  photo:          'rgba(59,130,246,0.5)',
  name:           'rgba(16,185,129,0.5)',
  title:          'rgba(245,158,11,0.5)',
  searchQuestion: 'rgba(239,68,68,0.5)',
  message:        'rgba(168,85,247,0.5)',
  recommendation: 'rgba(236,72,153,0.5)',
};

const ZONE_BORDER: Record<TemplateZoneField, string> = {
  photo:          '#3b82f6',
  name:           '#10b981',
  title:          '#f59e0b',
  searchQuestion: '#ef4444',
  message:        '#a855f7',
  recommendation: '#ec4899',
};

// ── Default zone positions (sensible starting layout) ────────────────────────

const DEFAULT_ZONES: Record<TemplateZoneField, { x: number; y: number; w: number; h: number }> = {
  photo:          { x: 5,  y: 25, w: 22, h: 40 },
  name:           { x: 5,  y: 70, w: 55, h: 10 },
  title:          { x: 5,  y: 80, w: 55, h: 7 },
  searchQuestion: { x: 5,  y: 90, w: 90, h: 6 },
  message:        { x: 30, y: 30, w: 40, h: 15 },
  recommendation: { x: 30, y: 50, w: 40, h: 10 },
};

// ── Props ────────────────────────────────────────────────────────────────────

interface TemplateZoneEditorProps {
  templatePdfUrl: string;
  zones: TemplateZone[];
  onChange: (zones: TemplateZone[]) => void;
  primaryColor: string;
}

// ── Draggable zone overlay ───────────────────────────────────────────────────

const DraggableZone: React.FC<{
  zone: TemplateZone;
  containerRect: DOMRect | null;
  selected: boolean;
  onSelect: () => void;
  onUpdate: (patch: Partial<TemplateZone>) => void;
}> = ({ zone, containerRect, selected, onSelect, onUpdate }) => {
  const dragRef = useRef<{
    mode: 'move' | 'resize';
    startX: number; startY: number;
    origX: number; origY: number; origW: number; origH: number;
  } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent, mode: 'move' | 'resize') => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origX: zone.x,
      origY: zone.y,
      origW: zone.w,
      origH: zone.h,
    };
  }, [zone, onSelect]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current || !containerRect) return;
    const dx = ((e.clientX - dragRef.current.startX) / containerRect.width) * 100;
    const dy = ((e.clientY - dragRef.current.startY) / containerRect.height) * 100;

    if (dragRef.current.mode === 'move') {
      onUpdate({
        x: Math.max(0, Math.min(100 - zone.w, dragRef.current.origX + dx)),
        y: Math.max(0, Math.min(100 - zone.h, dragRef.current.origY + dy)),
      });
    } else {
      onUpdate({
        w: Math.max(5, Math.min(100 - zone.x, dragRef.current.origW + dx)),
        h: Math.max(3, Math.min(100 - zone.y, dragRef.current.origH + dy)),
      });
    }
  }, [containerRect, zone.x, zone.y, zone.w, zone.h, onUpdate]);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const meta = ZONE_FIELDS.find((f) => f.field === zone.field);
  const borderColor = ZONE_BORDER[zone.field];
  const bgColor = ZONE_COLORS[zone.field];

  return (
    <div
      style={{
        position: 'absolute',
        left: `${zone.x}%`,
        top: `${zone.y}%`,
        width: `${zone.w}%`,
        height: `${zone.h}%`,
        background: bgColor,
        border: `2px ${selected ? 'solid' : 'dashed'} ${borderColor}`,
        borderRadius: 6,
        cursor: 'move',
        zIndex: selected ? 20 : 10,
        transition: dragRef.current ? 'none' : 'border-style 0.15s',
      }}
      onPointerDown={(e) => handlePointerDown(e, 'move')}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* Label */}
      <div
        style={{
          position: 'absolute',
          top: -1,
          left: -1,
          background: borderColor,
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          padding: '1px 6px',
          borderRadius: '4px 0 4px 0',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          lineHeight: '16px',
        }}
      >
        {meta?.icon} {meta?.label}
      </div>

      {/* Resize handle (bottom-right) */}
      <div
        style={{
          position: 'absolute',
          bottom: -4,
          right: -4,
          width: 10,
          height: 10,
          background: borderColor,
          borderRadius: 2,
          cursor: 'nwse-resize',
        }}
        onPointerDown={(e) => handlePointerDown(e, 'resize')}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
    </div>
  );
};

// ── Main editor ──────────────────────────────────────────────────────────────

const TemplateZoneEditor: React.FC<TemplateZoneEditorProps> = ({
  templatePdfUrl,
  zones,
  onChange,
  primaryColor,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);
  const [selectedField, setSelectedField] = useState<TemplateZoneField | null>(null);
  const [pdfLoaded, setPdfLoaded] = useState(false);

  // Render PDF onto canvas
  useEffect(() => {
    let destroyed = false;
    void (async () => {
      try {
        const pdf = await loadPdfDocumentFromUrl(templatePdfUrl);
        if (destroyed) { pdf.destroy?.(); return; }
        const page = await pdf.getPage(1);
        const canvas = canvasRef.current;
        if (!canvas || destroyed) return;
        const baseVp = page.getViewport({ scale: 1 });
        const scale = Math.min(600 / baseVp.width, 400 / baseVp.height);
        const vp = page.getViewport({ scale });
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        await page.render({ canvas, viewport: vp, background: '#0a0f1a' }).promise;
        if (!destroyed) setPdfLoaded(true);
        pdf.destroy?.();
      } catch {
        /* PDF load failed */
      }
    })();
    return () => { destroyed = true; };
  }, [templatePdfUrl]);

  // Track container rect for drag calculations
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('scroll', update, { passive: true });
    return () => { ro.disconnect(); window.removeEventListener('scroll', update); };
  }, [pdfLoaded]);

  const activeFields = useMemo(() => new Set(zones.map((z) => z.field)), [zones]);

  const addZone = useCallback((field: TemplateZoneField) => {
    if (activeFields.has(field)) return;
    const def = DEFAULT_ZONES[field];
    const meta = ZONE_FIELDS.find((f) => f.field === field);
    const newZone: TemplateZone = {
      field,
      ...def,
      color: meta?.defaultColor,
      align: field === 'photo' ? 'center' : 'left',
    };
    onChange([...zones, newZone]);
    setSelectedField(field);
  }, [zones, activeFields, onChange]);

  const removeZone = useCallback((field: TemplateZoneField) => {
    onChange(zones.filter((z) => z.field !== field));
    if (selectedField === field) setSelectedField(null);
  }, [zones, selectedField, onChange]);

  const updateZone = useCallback((field: TemplateZoneField, patch: Partial<TemplateZone>) => {
    onChange(zones.map((z) => (z.field === field ? { ...z, ...patch } : z)));
  }, [zones, onChange]);

  return (
    <div className="flex flex-col gap-4">
      {/* PDF preview with zone overlays */}
      <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-900">
        <div ref={containerRef} className="relative" style={{ minHeight: 200 }}>
          <canvas
            ref={canvasRef}
            className="block w-full h-auto"
            style={{ display: pdfLoaded ? 'block' : 'none' }}
          />
          {!pdfLoaded && (
            <div className="flex items-center justify-center h-[200px] text-slate-500 text-sm">
              Template laden...
            </div>
          )}

          {/* Zone overlays */}
          {pdfLoaded && zones.map((zone) => (
            <DraggableZone
              key={zone.field}
              zone={zone}
              containerRect={containerRect}
              selected={selectedField === zone.field}
              onSelect={() => setSelectedField(zone.field)}
              onUpdate={(patch) => updateZone(zone.field, patch)}
            />
          ))}
        </div>
      </div>

      {/* Zone buttons: add fields */}
      <div>
        <p className="text-[11px] font-semibold text-slate-500 mb-2">
          Klik om een veld toe te voegen — sleep en resize op de template:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {ZONE_FIELDS.map((f) => {
            const active = activeFields.has(f.field);
            return (
              <button
                key={f.field}
                type="button"
                onClick={() => active ? removeZone(f.field) : addZone(f.field)}
                className={`px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border transition-all ${
                  active
                    ? 'text-white border-transparent shadow-sm'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
                style={active ? { background: ZONE_BORDER[f.field] } : undefined}
                title={active ? `${f.label} verwijderen` : `${f.label} toevoegen`}
              >
                {f.icon} {f.label} {active ? '✕' : '+'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected zone settings */}
      {selectedField && (() => {
        const zone = zones.find((z) => z.field === selectedField);
        if (!zone) return null;
        const meta = ZONE_FIELDS.find((f) => f.field === selectedField);
        return (
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-[12px] font-bold text-slate-700 mb-2">{meta?.icon} {meta?.label} instellingen</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-slate-500">Tekstkleur</span>
                <input
                  type="color"
                  value={zone.color || '#ffffff'}
                  onChange={(e) => updateZone(zone.field, { color: e.target.value })}
                  className="w-full h-8 rounded border border-slate-200 cursor-pointer"
                />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] text-slate-500">Uitlijning</span>
                <select
                  value={zone.align || 'left'}
                  onChange={(e) => updateZone(zone.field, { align: e.target.value as 'left' | 'center' | 'right' })}
                  className="h-8 rounded border border-slate-200 text-[12px] px-2"
                >
                  <option value="left">Links</option>
                  <option value="center">Midden</option>
                  <option value="right">Rechts</option>
                </select>
              </label>
              {zone.field !== 'photo' && (
                <label className="flex flex-col gap-0.5 col-span-2">
                  <span className="text-[10px] text-slate-500">Lettergrootte (px bij 1920px breed)</span>
                  <input
                    type="number"
                    min={10}
                    max={200}
                    value={zone.fontSize || (zone.field === 'name' ? 64 : zone.field === 'title' ? 32 : 20)}
                    onChange={(e) => updateZone(zone.field, { fontSize: parseInt(e.target.value) || 20 })}
                    className="h-8 rounded border border-slate-200 text-[12px] px-2"
                  />
                </label>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TemplateZoneEditor;
